/**
 * What the sheet shows for a given recording state: its line of status, and whether the third
 * control offers Resume rather than Pause. The test file pins both on one row for every flag
 * combination, because a line reading "tap Resume" above a button labelled Pause is a dead end.
 * The sheet's two calls to them are covered by recording-pause-resume.e2e.ts, which injects the
 * interrupted state, since only native code raises it.
 */

type RecordingSheetFlags = {
    isPaused?: boolean;
    isInterrupted?: boolean;
    needsManualResume?: boolean;
    isMinimized?: boolean;
};

/**
 * Resume replaces Pause once the caregiver has held a recording, and also once nothing will resume
 * it by itself, where Resume is the only recovery. Note what that costs: returning to the app still
 * restarts an Android recording, and without Pause there is no longer a way to refuse it.
 */
export const shouldOfferResume = ({ isPaused, isInterrupted, needsManualResume }: RecordingSheetFlags): boolean =>
    Boolean(isPaused || (isInterrupted && needsManualResume));

export const getRecordingStatusText = ({
    isPaused,
    isInterrupted,
    needsManualResume,
    isMinimized
}: RecordingSheetFlags): string => {
    if (isPaused) {
        // Their own pause wins: it is the one state the phone will not undo.
        return 'Paused';
    }

    if (!isInterrupted) {
        return 'Recording';
    }

    // The full sentence does not fit the minimised sheet, where the pause icon plus the short
    // form still says who stopped it.
    if (isMinimized) {
        return 'Paused by your phone';
    }

    // Set once nothing will resume it: Android losing audio focus for good, or a resume still
    // refused once the interruption is over. Naming Resume is safe because shouldOfferResume reads
    // these same flags and renders it. Until then the hedged promise is the honest one.
    if (needsManualResume) {
        return 'Paused by your phone. Will not resume by itself: tap Resume.';
    }

    return 'Paused by your phone. Usually resumes by itself.';
};
